import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { EditableReviewItem } from "../../../common/components/EditableReviewItem";
import SSNMask from "../../../common/components/mask/SSNMask";
import MobileNumberMask from "../../../common/components/mask/MobileNumberMask";
import AddressZipMask from "../../../common/components/mask/AddressZipMask";
import { gql, useMutation } from "@apollo/client";
import clientGraphql from "../../../../common/apollo-graphql";
import Swal from "sweetalert2";
import { isEmpty } from "lodash";
import { ReviewItem } from "../../../clinics/Management/EnrollClinic/ReviewInformation";
import StringUtils from "../../../common/services/StringUtils";
import { MANUALLY_CREATED_STATUS } from "../../constants/referral.constants";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";

const normalizeDigits = (value) => (value == null ? "" : String(value)).replace(/\D/g, "");
const normalizeDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") return value;
  if (typeof value?.toDate === "function") return value.toDate();
  return value;
};

const buildSchema = (fields) => {
  const shape = {};
  fields.forEach(({ field, kind }) => {
    if (kind === "phone") {
      shape[field] = yup
        .string()
        .nullable()
        .test("phone-length", "Phone number must have 10 digits", (value) => {
          if (!value) return true;
          return normalizeDigits(value).length === 10;
        });
    }
    if (kind === "date") {
      shape[field] = yup
        .mixed()
        .test("valid-date", "Date is invalid.", (value) => {
          const normalized = normalizeDateValue(value);
          if (!normalized) return true;
          return moment(normalized).isValid();
        });
    }
  });
  return yup.object().shape(shape);
};

export function ReferralHeader({ parsedData, currentUser }) {
  const clinicId = !isEmpty(parsedData)
    ? parsedData.referral.getClinic(currentUser)
    : '';

  if (!clinicId) return '';

  const items = {
    'clinic': parsedData?.referral?.getClinicName(currentUser) && parsedData.referral.getClinicName(currentUser),
    'provider': parsedData?.referral?.getProviderName(currentUser),
    'address': parsedData?.referral?.getClinicAddress(currentUser) && parsedData.referral.getClinicAddress(currentUser),
    'office_phone': parsedData?.referral?.getClinicOfficePhone(currentUser) && parsedData.referral.getClinicOfficePhone(currentUser),
    'fax': parsedData?.referral?.getClinicFax(currentUser) && parsedData.referral.getClinicFax(currentUser),
    'sender': parsedData?.referrer?.metadata?.contact && parsedData.referrer.metadata.contact,
    'email': parsedData?.referrer?.metadata?.contact_email && parsedData.referrer.metadata.contact_email,
    'phone': parsedData?.referrer?.metadata?.office_phone && parsedData.referrer.metadata.office_phone,
    'fax_#': parsedData?.referrer?.metadata?.fax && parsedData.referrer.metadata.fax,
  };

  return (
    <>
      {Object.entries(items).map(([key, value]) => {
        if (!value) return null;

        const formattedLabel = StringUtils.toTitleCase(key);

        return <ReviewItem key={key} label={formattedLabel} value={value} />;
      })}

    </>
  );
}

const ReferralInfoForm = ({ details, edit, referralUuid, setIsEditMode, refetch, setSaving }) => {
  const [mergedDetails, setMergedDetails] = useState({});
  const fields = useMemo(() => [
    { filed: "clinic_id", label: "Clinic", kind: "clinic" },
    { field: "insurance_provider", label: "Insurance Provider", kind: "text" },
    { field: "policy_number", label: "Policy Number", kind: "text" },
    { field: "insurance_provider2", label: "Secondary Insurance Provider", kind: "text", forManuallyCreated: true },
    { field: "policy_number2", label: "Secondary Policy Number", kind: "text", forManuallyCreated: true },
    { field: "diagnosis", label: "Diagnosis", kind: "text" },
    { field: "comment", label: "Comment", kind: "text" },
    ...(details?.referralExtraInfo
      ? Object.entries(details.referralExtraInfo).map(([key, value]) => ({
        field: key,
        label: value.label,
        kind: value.kind,
      }))
      : []),
  ], [details]);

  const validationSchema = useMemo(() => buildSchema(fields), [fields]);
  const { control, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: { ...details },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const SAVE_REFERRAL_MUTATION = gql`
    mutation ($params: String!) {
      save_referral(params: $params)
    }
  `;

  const [saveReferral] = useMutation(SAVE_REFERRAL_MUTATION, { client: clientGraphql });

  const getLabel = (custom) => custom.label;

  const getInputProps = useCallback(fieldName => {
    if (fieldName === 'ssn') return { inputComponent: SSNMask };
    if (fieldName === 'phone') return { inputComponent: MobileNumberMask };
    if (fieldName === 'address_zip') return { inputComponent: AddressZipMask };
    return null;
  }, []);

  const onSubmit = (data) => {
    setSaving(true);
    const renderedFields = ["insurance_provider", "policy_number", "insurance_provider2", "policy_number2", "diagnosis", "comment"];
    const normalizeValue = (value) => (value == null ? "" : value);
    const filteredData = {};
    const changedFields = [];
    const customKeys = Object.keys(data).filter((key) => key.startsWith("custom_"));
    const allFields = [...renderedFields, ...customKeys];

    allFields.forEach((key) => {
      const value = data[key];
      const normalizedValue = normalizeValue(value);
      const normalizedOriginal = normalizeValue(mergedDetails?.[key]);
      if (normalizedValue !== normalizedOriginal) {
        filteredData[key] = normalizedValue;
        changedFields.push(key);
      }
    });
    filteredData.uuid = referralUuid;
    filteredData.status = details.status;
    
    const hasMatch = details?.parsedData?.referral?.hasMatch(details?.currentUser);
    const clinicType = hasMatch !== null ? (hasMatch ? "clinic_receiver_id" : "clinic_referrer_id") : null;
    const providerType = hasMatch !== null ? (hasMatch ? "provider_id" : "referrer_provider_id") : null;

    if (clinicType) {
      const normalizedValue = normalizeValue(data.clinic_id);
      const normalizedOriginal = normalizeValue(mergedDetails?.clinic_id);
      if (normalizedValue !== normalizedOriginal) {
        filteredData[clinicType] = normalizedValue;
        changedFields.push(clinicType);
      }
    }
    if (providerType) {
      const normalizedValue = normalizeValue(data.provider_id);
      const normalizedOriginal = normalizeValue(mergedDetails?.provider_id);
      if (normalizedValue !== normalizedOriginal) {
        filteredData[providerType] = normalizedValue;
        changedFields.push(providerType);
      }
    }

    if (changedFields.length === 0) {
      setSaving(false);
      setIsEditMode(false);
      return;
    }

    saveReferral({ variables: { params: JSON.stringify(filteredData) } })
      .then(() => {
        refetch();
        Swal.fire({ icon: 'success', text: "Saved Referral Information.", showConfirmButton: true });
        setIsEditMode(false);
      })
      .catch((error) => {
        console.error(error);
        Swal.fire("Failed to save referral information.");
      });
  };

  useEffect(() => {
    if (details) {
      let clinicId = details.parsedData?.referral?.getClinicId(details.currentUser)
      let providerId = details.parsedData?.referral?.getProviderId(details.currentUser)
      const newMergedDetails = {
        ...details,
        ...(details.referralExtraInfo
          ? Object.fromEntries(
            Object.entries(details.referralExtraInfo).map(([key, value]) => [key, value.value])
          )
          : {}),
        clinic_id: clinicId,
        provider_id: providerId,
      };
      setMergedDetails(newMergedDetails);
      reset(newMergedDetails);
    }
  }, [details, reset]);

  return (
    <form id="referral-info-form" onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1}>
        {!edit &&
          <ReferralHeader parsedData={details?.parsedData} currentUser={details?.currentUser} />
        }

        {fields.map(({ field, label, kind, forManuallyCreated }) => {
          if (field !== "clinic_id" || details.status === MANUALLY_CREATED_STATUS) {
            return (!forManuallyCreated || (forManuallyCreated && details.is_manually_created) ? (
              <EditableReviewItem
                key={field}
                edit={edit}
                custom={{ field, label, kind }}
                control={control}
                errors={errors}
                getLabel={getLabel}
                getInputProps={getInputProps}
                label={label}
                value={mergedDetails[field]}
                watch={watch}
              />
            ) : null);
          }

          return null;
        })}
      </Grid>
      {edit && <button type="submit" style={{ display: "none" }} />}
    </form>
  );
};

export default ReferralInfoForm;
